/** DriverView — wrapper for driver detail with tab navigation (Races / Stats / Pace) */
import { useState, useEffect, useCallback } from 'react';
import { rpc } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ArrowLeft, ListOrdered, Timer, BarChart3 } from 'lucide-react';
import { RpcError } from '@/api/client';
import type { GetDriverRaceHistoryResponse, DriverRaceResult } from '@/types/telemetry';

interface DriverViewProps {
  driverId: string;
  activeTab: 'races' | 'stats' | 'pace';
  children?: React.ReactNode;
}

export function DriverView({ driverId, activeTab, children }: DriverViewProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const [driverInfo, setDriverInfo] = useState<{ id: string; firstName: string; lastName: string; nationality: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<GetDriverRaceHistoryResponse>('GetDriverRaceHistory', { driver_id: driverId, page: 1, page_size: 1 });
      setDriverInfo(res.driver);
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested driver could not be found.' : (e.message || t('races.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading && !driverInfo) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!driverInfo) return <ErrorState message={t('races.noData')} />;

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => window.history.back()}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      {/* Driver name header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
          <span className="text-lg font-bold gradient-text">
            {driverInfo.firstName.charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{driverInfo.firstName} {driverInfo.lastName}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('races.driverHistory')}</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex items-center gap-1 mb-6">
        <TabButton
          active={activeTab === 'races'}
          icon={<ListOrdered size={13} />}
          label={t('races.tabRaces')}
          onClick={() => navigate(`#/races/driver/${driverId}/races`)}
        />
        <TabButton
          active={activeTab === 'stats'}
          icon={<BarChart3 size={13} />}
          label={t('races.tabStats')}
          onClick={() => navigate(`#/races/driver/${driverId}/stats`)}
        />
        <TabButton
          active={activeTab === 'pace'}
          icon={<Timer size={13} />}
          label={t('races.tabPace')}
          onClick={() => navigate(`#/races/driver/${driverId}/pace`)}
        />
      </div>

      {/* Render children */}
      {children}
    </div>
  );
}

function TabButton({ active, icon, label, onClick }: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
        ${active ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
      `}
    >
      {icon}
      {label}
    </button>
  );
}
